// Google Analytics 4, and the consent it waits for. This file is the whole of it:
// remove it, the calls in entrypoints/application.js, CookieNotice.vue and the two
// footers, and the Google hosts in config/initializers/content_security_policy.rb,
// and no analytics remain. Switching properties is GOOGLE_ANALYTICS_ID and the
// property settings this code assumes — both in docs/analytics.md. The notice now
// saves an answer per category rather than one accept/decline choice; the container
// rules that govern what each category is allowed to load live in docs/analytics.md.
//
// Nothing is fetched until the visitor says yes. gtag.js writes its _ga cookies the
// moment it runs, and a cookie that isn't strictly necessary needs permission first
// (art. 11.7a Telecommunicatiewet), so consent decides whether the script is ever
// requested — rather than it loading and consent being asked around it.
import { computed, ref } from "vue";
import { useStorage } from "@vueuse/core";

// The property is configuration, not a constant: Pandig::Configuration reads
// GOOGLE_ANALYTICS_ID and app/helpers/analytics_helper.rb renders it into the
// layout. Development and test leave it unset, so nothing here can run there —
// which is the point, since a developer's own browsing would otherwise be
// indistinguishable from a visitor's in the same property.
function measurementId() {
  return document.querySelector('meta[name="google-analytics-id"]')?.content?.trim() ?? "";
}

function containerId() {
  return document.querySelector('meta[name="google-tag-manager-id"]')?.content?.trim() ?? "";
}

export function analyticsConfigured() {
  return measurementId() !== "" || containerId() !== "";
}

// The stored version ties an answer to the exact first-layer information the visitor
// saw. A materially changed purpose or recipient gets a new version, which asks again
// instead of stretching old permission — hence the bump when one Accepteren button
// became two separate categories: that older yes was never asked about marketing, and
// it was stored in a shape (`choice`) this code no longer reads. One year is a
// conservative refresh interval; the EDPB recommends asking again at intervals.
const CONSENT_VERSION = "2026-08-09-2";
const CONSENT_MAX_AGE = 365 * 24 * 60 * 60 * 1000;
const DENIED_CONSENT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

// The public website, and only that: Privacybeleid.vue §5/§6 and the
// verwerkersovereenkomst both promise that no measurement runs in the signed-in
// environment, so nothing a member, unit, mandate or payment appears on is
// measured. Hand-synced with HomeController::CACHEABLE_PATHS, which draws the same
// line server-side — spec/requests/public_pages_spec.rb notices when they drift.
// The auth flows are deliberately out too: they are no-store, no-referrer pages.
const MEASURED_PATHS = ["/", "/voor-vve-beheerders", "/vve-betalingsachterstand", "/algemene-voorwaarden", "/privacybeleid", "/verwerkersovereenkomst"];

export const cookieConsent = useStorage("pandig-cookie-consent", {statistics: false, marketing: false, at: "", version: ""});

// A stored "null" (useStorage's serializer turns that literal string into JS null)
// is a corrupted record, not an absent one — cookieConsent.value can be nullish for
// a moment even though the default is an object. Falling through to {} keeps the
// fail direction "unanswered" instead of throwing partway through render.
const consentCurrent = computed(() => {
  const record = cookieConsent.value ?? {};
  const age = Date.now() - Date.parse(record.at);
  return record.version === CONSENT_VERSION && age >= 0 && age <= CONSENT_MAX_AGE;
});

// Refusing everything is an answer too, so "answered" cannot be read off the
// categories — it is the timestamp, which consentCurrent already requires to be
// parseable and current. A record that passes it is one the visitor gave.
export const consentAnswered = computed(() => consentCurrent.value);
export const statisticsAccepted = computed(() => consentCurrent.value && cookieConsent.value?.statistics === true);
export const marketingAccepted = computed(() => consentCurrent.value && cookieConsent.value?.marketing === true);

// Reopening the notice from the footer link the privacy statement (§9) promises.
// Not persisted: it lasts as long as the visitor is looking at the card.
export const noticeReopened = ref(false);

export function openCookieNotice() {
  noticeReopened.value = true;
}

export function saveConsent({statistics, marketing}) {
  cookieConsent.value = {
    statistics: statistics === true,
    marketing: marketing === true,
    at: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  noticeReopened.value = false;
  if (allowed()) apply();
  else withdraw();
  if (!statisticsAccepted.value) forgetCookies();
}

export function denyAll() {
  saveConsent({statistics: false, marketing: false});
}

// The counterweight to denyAll, and the reason it exists: with both switches starting
// off, refusing everything was one click while allowing everything was three. Consent
// has to be as easy to give as to withhold (AP, "Heldere cookiebanners"), so each
// answer is now one click on an identically styled button.
export function acceptAll() {
  saveConsent({statistics: true, marketing: true});
}

// Both hooks close different gaps. beforeEach silences the tag before a route into
// the dashboard is committed. afterEach sends the deliberately manual, sanitised
// public page view. GA's automatic "page changes based on browser history" setting
// must stay off; docs/analytics.md makes that property-side control explicit.
export function startAnalytics(router) {
  path = "";
  pageViewPending = false;
  previousPage = sanitiseReferrer(document.referrer);
  router.beforeEach((to) => {
    if (!measured(to.path)) silence();
  });
  router.afterEach((to) => {
    path = to.path;
    pageViewPending = measured(path);
    if (!pageViewPending) previousPage = "";
    apply();
  });
}

let path = "";
let pageViewPending = false;
let previousPage = sanitiseReferrer(document.referrer);

function apply() {
  if (allowed() && measured(path)) load();
  else silence();
}

function load() {
  prepareTag();
  window.gtag("consent", "update", consentState());
  window.gtag("set", "ads_data_redaction", !marketingAccepted.value);

  if (statisticsAccepted.value) loadAnalytics();
  else disableAnalytics();

  loadTagManager();

  if (pageViewPending && statisticsAccepted.value) sendPageView();
}

// One switch, one purpose. Advertising was hard-denied here until the notice grew a
// Marketing row; it stays denied by default and only a saved yes moves it, so a tag
// added to the container later is already gated rather than needing a new banner.
function consentState() {
  const advertising = marketingAccepted.value ? "granted" : "denied";

  return {
    ad_storage: advertising,
    ad_user_data: advertising,
    ad_personalization: advertising,
    analytics_storage: statisticsAccepted.value ? "granted" : "denied",
  };
}

// Google's install snippet is an inline <script>; this is the same work from the
// bundle, which is what the CSP allows (script_src :self plus the Google hosts, no
// unsafe-inline). Consequence worth knowing before configuring tags: Custom HTML tags
// inside the container will not run either.
//
// The container is requested for either allowed category — it is the delivery
// mechanism, not a purpose of its own — and Consent Mode above decides what its tags
// may store. It deliberately carries no GA4 configuration tag: gtag.js above is the
// GA4 load, and both would count every page view twice (docs/analytics.md).
function loadTagManager() {
  const id = containerId();
  if (!id) return;

  const src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  if (document.querySelector(`script[src="${src}"]`)) return;

  window.dataLayer.push({"gtm.start": Date.now(), event: "gtm.js"});
  inject(src);
}

function loadAnalytics() {
  const id = measurementId();
  if (!id) return;

  window[`ga-disable-${id}`] = false;
  const src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  if (document.querySelector(`script[src="${src}"]`)) return;

  window.gtag("js", new Date());
  // Page views are manual so URLs can be reduced to origin + approved path. Ads
  // remain denied in Consent Mode as well as disabled in the Analytics config.
  window.gtag("config", id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  inject(src);
}

function inject(src) {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.append(script);
}

function measured(candidate) {
  return MEASURED_PATHS.includes(candidate);
}

function allowed() {
  return statisticsAccepted.value || marketingAccepted.value;
}

function prepareTag() {
  window.dataLayer = window.dataLayer || [];
  if (window.gtag) return;

  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  // These defaults are queued before gtag.js is requested, so every advertising
  // purpose is denied from its first instruction. Only analytics_storage changes.
  window.gtag("consent", "default", DENIED_CONSENT);
  window.gtag("set", "ads_data_redaction", true);
}

function sendPageView() {
  const pageLocation = `${location.origin}${path}`;
  window.gtag("set", "page_location", pageLocation);
  window.gtag("set", "page_referrer", previousPage);
  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: pageLocation,
    page_referrer: previousPage,
  });
  previousPage = pageLocation;
  pageViewPending = false;
}

// Keep arbitrary query strings and fragments out of Analytics. They are not needed
// for the published purpose and can accidentally contain an email address or token.
// For an outside referrer, its origin is enough to report the source site.
function sanitiseReferrer(value) {
  if (!value) return "";

  try {
    const referrer = new URL(value);
    if (!/^https?:$/.test(referrer.protocol)) return "";
    if (referrer.origin === location.origin && measured(referrer.pathname)) {
      return `${referrer.origin}${referrer.pathname}`;
    }
    return referrer.origin;
  } catch {
    return "";
  }
}

function withdraw() {
  silence();
  forgetCookies();
}

// Leaving the public website, or standing down after a refusal. Denying through
// Consent Mode tells whatever is already loaded that permission changed; ga-disable
// is Google's documented kill switch, which stops the GA4 copy running on this page
// from sending anything further — something a removed <script> tag would not do.
function silence() {
  if (window.gtag) {
    window.gtag("consent", "update", DENIED_CONSENT);
    window.gtag("set", "ads_data_redaction", true);
  }
  disableAnalytics();
}

function disableAnalytics() {
  const id = measurementId();
  if (id) window[`ga-disable-${id}`] = true;
}

// _ga is set on the registrable domain (.pandig.nl) and _ga_<property> alongside
// it, so expiring them takes the same domain the browser stored them under — hence
// every candidate, not just the current hostname.
function forgetCookies() {
  const domains = ["", location.hostname, `.${location.hostname}`, `.${location.hostname.split(".").slice(-2).join(".")}`];

  for (const pair of document.cookie.split("; ")) {
    const name = pair.split("=")[0];
    if (!name.startsWith("_ga")) continue;

    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
}
